import { AxiosResponse } from "axios";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CheckIcon,
  CloseIcon,
  Divider,
  Down1Icon,
  DragDrop,
  GKDatePicker,
  GKSearchSelect,
  GKSelect,
  GKTextField,
  Grid2,
  IconButton,
  LockIcon,
  MenuItem,
  PhoneInput,
  SendIcon,
  Show,
  SuccessFilledIcon,
  Typography,
  WarningIcon,
} from "components";
import dayjs from "dayjs";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import {
  ClientResponseById,
  FpCreateKYCRequestResponse,
  FpOnboardingCreateKycRequest,
} from "interfaces/ClientOnboarding";
import { RightArrowIcon } from "Pages/Website/Icons";
import { useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "store/store";
import axiosInstance from "utils/axiosInstance";
import {
  incomeSlabOptions,
  occupationOptions,
  pepDetailsOptions,
} from "utils/common";
import {
  BANK_ACCOUNT_TYPES,
  MARITAL_STATUS,
  PERSONAL_GENDER,
} from "utils/constants";
import { QueryKeys } from "utils/queryKey";
import {
  emailRegex,
  panRegex,
  validateNotWhitespaceOnly,
} from "utils/validationSchema";

const AdvisorIncompleteMFKYC = ({
  id,
  setOpen,
  maxHeight,
  logo,
}: {
  id: string;
  setOpen?: (d: boolean) => void;
  maxHeight?: string;
  logo?: string;
}) => {
  const form = useForm<
    ClientResponseById &
      FpCreateKYCRequestResponse & { currentStep: 0 | 1; aadharCardPdf: any[] }
  >();
  const countryList = useAppSelector((state) => state?.app?.countryList);
  const dispatch = useAppDispatch();
  const [expanded, setExpanded] = useState<number | false>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Ensure country list is fetched if not already available
  useFetchQuery({
    key: ["GET_ALL_COUNTRIES"],
    route: "/country/",
    enabled: !countryList?.length, // Only fetch if countryList is empty
    success: (data: any) => {
      dispatch({ type: "COUNTRY_LIST", data: data.data });
    },
  });

  // Fallback country data if Redux store is empty
  const fallbackCountries = [
    { id: 102, name: "India", code: "IN", countryCode: "IN" },
    { id: 231, name: "United States", code: "US", countryCode: "US" },
    { id: 77, name: "United Kingdom", code: "GB", countryCode: "GB" },
    { id: 38, name: "Canada", code: "CA", countryCode: "CA" },
    { id: 13, name: "Australia", code: "AU", countryCode: "AU" },
    { id: 61, name: "Germany", code: "DE", countryCode: "DE" },
    { id: 68, name: "France", code: "FR", countryCode: "FR" },
    { id: 153, name: "Singapore", code: "SG", countryCode: "SG" },
    { id: 184, name: "UAE", code: "AE", countryCode: "AE" },
  ];

  const countriesToShow =
    countryList?.length > 0 ? countryList : fallbackCountries;

  const handleChange = (panel: number) => (_: any, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const { fields } = useFieldArray({
    control: form.control,
    name: "bankAccounts",
  });

  const {
    fields: nomineeFields,
    append: appendNominee,
    remove: removeNominee,
  } = useFieldArray({
    control: form.control,
    name: "nominees",
  });

  useEffect(() => {
    if (nomineeFields.length === 0) {
      appendNominee({ name: "", percentage: "", relationship: "" });
    }
  }, []);

  // XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===
  const { isFetching } = useFetchQuery<AxiosResponse<ClientResponseById>>({
    key: [QueryKeys.GET_CLIENT_BY_ID, id],
    route: `/client/${id}/`,
    enabled: Boolean(id),
    success: (data: { data: ClientResponseById }) => {
      form.reset({ ...data?.data, currentStep: 0 });
    },
  });

  // ===========================================================================================
  const handlePreRequestMFD = useMutation({
    mutationKey: ["HANDLE_CREATE_KYC_REQUEST"],
    mutationFn: (clientState: FpOnboardingCreateKycRequest) =>
      axiosInstance.post(
        `/api/fp-onboarding/sessions/${form.getValues("fpOnboardingSession.id")}/create-kyc-request/`,
        {
          ...clientState,
        },
      ),
    onSuccess: (response: { data: { data: FpCreateKYCRequestResponse } }) => {
      form.reset({
        ...form.getValues(),
        ...response.data.data,
        currentStep: 1,
      });
    },
  });
  // ===========================================================================================
  const handleStepsApis = useMutation({
    mutationFn: (data: any) => data,
    onSuccess: (response: any) => {
      const res = response?.data?.data;
      console.log("🚀 ~ AdvisorIncompleteMFKYC ~ res:", res);
      // Open redirectUrl if available in response
      if (res?.redirectUrl) {
        window.open(res.redirectUrl, "_blank");
      }
    },
    onError: (error) => {
      console.log("🚀 ~ AdvisorIncompleteMFKYC ~ error:", error);
    },
  });
  form.watch();

  const stepperData = [
    {
      title: "Verify Identity",
      subTitle:
        "Validate your Aadhaar-linked identity at UIDAI's official portal.",
      isCompleted:
        form.getValues("fpOnboardingSession.fpIdentityDocumentFetchStatus") ===
        "successful",
      api: "",
      icon: WarningIcon,
      card: (
        <Card
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            alignItems: "flex-start",
          }}
        >
          <Alert
            severity="warning"
            sx={{
              border: "none",
              p: 2,
              "& .MuiAlert-icon": { display: "none" },
            }}
          >
            Verification pending. Please complete the OTP step on the UIDAI
            portal and return.
          </Alert>
          <Typography>
            {
              "You'll be redirected to an external site. Complete the OTP verification and return here to continue."
            }
          </Typography>
          <Button
            endIcon={<SendIcon />}
            variant="contained"
            onClick={() => {
              handleStepsApis.mutate(
                axiosInstance.post(
                  `/api/fp-onboarding/sessions/${form.getValues("fpOnboardingSession.id")}/kyc-request-identity-document/`,
                ),
              );
            }}
          >
            Visit Now
          </Button>
        </Card>
      ),
    },
    {
      title: "Upload Aadhar Card",
      subTitle: "Please upload Aadhar card to complete your KYC",
      isCompleted: !!form.getValues("fpOnboardingSession.fpIdentityDocumentId"),
      api: "",
      icon:
        form.getValues("fpOnboardingSession.fpIdentityDocumentFetchStatus") ===
        "successful"
          ? WarningIcon
          : LockIcon,
      card: (
        <Box
          display={"flex"}
          flexDirection={"column"}
          gap={2}
          alignItems={"flex-end"}
        >
          <Controller
            name={`aadharCardPdf`}
            control={form.control}
            rules={{
              required: {
                value: true,
                message: "Please upload a document",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <DragDrop
                {...field}
                uploadError={uploadError}
                setUploadError={setUploadError}
                onChange={(e) => {
                  field.onChange(e);
                }}
                supportName=".png, .jpg, .pdf"
                acceptType={".pdf,.png,.jpg"}
                error={!!error}
              />
            )}
          />
          <Button
            startIcon={<CheckIcon />}
            variant="contained"
            onClick={() => {
              const formData = new FormData();
              formData.append("file", form.getValues("aadharCardPdf")[0]);

              handleStepsApis.mutate(
                axiosInstance.post(
                  `/api/fp-onboarding/sessions/${form.getValues("fpOnboardingSession.id")}/kyc-request-signature-file/`,
                  formData,
                ),
              );
            }}
          >
            Upload
          </Button>
        </Box>
      ),
    },
    {
      title: "eSign Application",
      subTitle:
        "Sign your KYC application digitally with your Aadhaar-linked mobile.",
      isCompleted:
        form.getValues("fpOnboardingSession.fpEsignStatus") === "successful",
      api: "",
      icon: form.getValues("fpOnboardingSession.fpIdentityDocumentId")
        ? WarningIcon
        : LockIcon,
      card: (
        <Card
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            alignItems: "flex-start",
          }}
        >
          <Alert
            severity="warning"
            sx={{
              border: "none",
              p: 2,
              "& .MuiAlert-icon": { display: "none" },
            }}
          >
            eSign pending. Please complete the signing process and return to
            continue.
          </Alert>
          <Typography>
            {
              "You'll be redirected to an external site. Complete the process and return here to continue."
            }
          </Typography>
          <Button
            endIcon={<SendIcon />}
            variant="contained"
            onClick={() => {
              handleStepsApis.mutate(
                axiosInstance.post(
                  `/api/fp-onboarding/sessions/${form.getValues("fpOnboardingSession.id")}/kyc-request-esign/`,
                ),
              );
            }}
          >
            Visit Now
          </Button>
        </Card>
      ),
    },
  ];

  return (
    <form
      onSubmit={form.handleSubmit((values) => {
        handlePreRequestMFD.mutate({
          name: values.personalDetails.name,
          pan: values.personalDetails.pan,
          email: values.personalDetails.email,
          date_of_birth: values.personalDetails.dateOfBirth,
          mobile: {
            isd: values.countryCode,
            number: values.phone,
          },
          gender: values.personalDetails.gender.toLowerCase(),
          father_name: values.personalDetails.fatherName,
          marital_status: values.personalDetails.maritalStatus.toLowerCase(),
          residential_status: values.personalDetails.residentialStatus,

          occupation_type: values.personalDetails.occupationType,
          citizenship_countries: [
            values.personalDetails.citizenshipCountries?.countryCode || "",
          ],
          nationality_country:
            values.personalDetails.nationalityCountry?.countryCode || "",
          country_of_birth:
            values.personalDetails.countryOfBirth?.countryCode || "",
          place_of_birth: values.personalDetails.placeOfBirth,
          income_slab: values.personalDetails.incomeSlab,
          pep_details: values.personalDetails.pepDetails,
          tax_residency_other_than_india:
            values.personalDetails.taxResidencyOtherThanIndia,
          geolocation: {
            latitude: 19.076,
            longitude: 72.8777,
          },
        });
      })}
    >
      <Grid2 container>
        <Show.When isTrue={!!setOpen}>
          <Grid2
            size={12}
            px={1}
            py={1}
            display={"flex"}
            justifyContent={"flex-end"}
          >
            <IconButton onClick={() => setOpen(false)}>
              <CloseIcon width={20} height={20} />
            </IconButton>
          </Grid2>
        </Show.When>
        <Grid2
          size={8}
          px={2}
          py={1}
          pt={0}
          display={"flex"}
          flexDirection={"column"}
          gap={1}
        >
          <Typography fontSize={20} fontWeight={700}>
            KYC Details
          </Typography>
          <Typography fontSize={12} color="var(--neutral-500)">
            Please provide accurate details. Incorrect information may delay
            your KYC approval.
          </Typography>
        </Grid2>
        <Show.When isTrue={!!logo}>
          <Grid2
            size={4}
            display={"flex"}
            alignContent={"center"}
            justifyContent={"flex-end"}
          >
            <img
              src={logo}
              alt=""
              height={`-webkit-fill-available`}
              width={"auto"}
              style={{ maxHeight: "32px" }}
              onError={({ currentTarget }) => {
                currentTarget.src = "https://gridkey.in/Logos/iconFullLogo.png";
              }}
            />
          </Grid2>
        </Show.When>
        <Show.When isTrue={form.getValues("currentStep") === 0}>
          <Grid2
            size={12}
            overflow={"auto"}
            p={2}
            maxHeight={maxHeight || "calc(100vh - 195px)"}
          >
            <form id="advisor-onboarding-client-details-form">
              <Grid2 container columnSpacing={2} rowSpacing={3}>
                <Grid2 size={12}>
                  <Controller
                    name="personalDetails.name"
                    control={form.control}
                    rules={{
                      required: {
                        value: true,
                        message: "Please enter name",
                      },
                      validate: validateNotWhitespaceOnly,
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <GKTextField
                        {...field}
                        loading={isFetching}
                        inputLabel="Name"
                        requiredField
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="personalDetails.pan"
                    rules={{
                      required: {
                        value: true,
                        message: "Please enter PAN",
                      },
                      pattern: {
                        value: panRegex,
                        message: "PAN should have this format ABCDE1234A",
                      },
                      maxLength: {
                        value: 10,
                        message: "PAN should have 10 character",
                      },
                      minLength: {
                        value: 10,
                        message: "PAN should have 10 character",
                      },
                    }}
                    control={form.control}
                    render={({ field, fieldState: { error } }) => (
                      <GKTextField
                        {...field}
                        loading={isFetching}
                        inputLabel="PAN"
                        onChange={(e) =>
                          field.onChange(e.target.value.toUpperCase())
                        }
                        requiredField
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="personalDetails.aadhar"
                    rules={{
                      required: {
                        value: true,
                        message: "Please enter Aadhaar",
                      },
                    }}
                    control={form.control}
                    render={({ field, fieldState: { error } }) => (
                      <GKTextField
                        {...field}
                        loading={isFetching}
                        inputLabel="Aadhaar No."
                        onChange={(e) =>
                          field.onChange(e.target.value.toUpperCase())
                        }
                        requiredField
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={12}>
                  <Divider />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="phone"
                    rules={{
                      required: {
                        value: true,
                        message: "Please enter phone number",
                      },
                    }}
                    control={form.control}
                    render={({ field }) => (
                      <PhoneInput
                        {...field}
                        onSelectChange={(event: any) => {
                          form.setValue("countryCode", event);
                        }}
                        onTextChange={(event: any) => {
                          field.onChange(event);
                        }}
                        inputLabel="Phone Number"
                        onTextValue={field.value}
                        requiredField
                        onSelectValue={form.getValues("countryCode")}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="personalDetails.email"
                    control={form.control}
                    rules={{
                      required: {
                        value: true,
                        message: "Please enter email",
                      },
                      pattern: {
                        value: emailRegex,
                        message: "Please enter a valid mail",
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <GKTextField
                        {...field}
                        loading={isFetching}
                        inputLabel="Email"
                        requiredField
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={12} display={"flex"} alignItems={"center"} gap={1}>
                  <Typography
                    color="var(--neutral-500)"
                    fontSize={12}
                    whiteSpace={"nowrap"}
                    fontWeight={600}
                  >
                    BANK DETAILS
                  </Typography>
                  <Divider sx={{ flex: 1 }} />
                </Grid2>
                <Grid2 size={12}>
                  {fields?.map((row: any, index) => {
                    return (
                      <Grid2 container size={12} spacing={1} key={row.id}>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                          <Controller
                            name={`bankAccounts.${index}.name`}
                            control={form.control}
                            rules={{
                              required: {
                                value: true,
                                message: "Please enter Account holder name",
                              },
                            }}
                            render={({ field, fieldState: { error } }) => (
                              <GKTextField
                                {...field}
                                loading={isFetching}
                                requiredField
                                inputLabel="Bank Account Name"
                                error={!!error}
                                helperText={error?.message}
                              />
                            )}
                          />
                        </Grid2>

                        <Grid2 size={{ xs: 12, md: 6 }}>
                          <Controller
                            name={`bankAccounts.${index}.type`}
                            control={form.control}
                            rules={{
                              required: {
                                value: true,
                                message: "Please select Account type",
                              },
                            }}
                            render={({ field, fieldState: { error } }) => (
                              <GKSelect
                                {...field}
                                requiredField
                                inputLabel="Bank Account Type"
                                error={!!error}
                                helperText={error?.message}
                              >
                                <MenuItem style={{ display: "none" }} value="">
                                  Select Account Type
                                </MenuItem>
                                {BANK_ACCOUNT_TYPES?.map((dataItem) => (
                                  <MenuItem
                                    value={dataItem?.value}
                                    key={dataItem?.value}
                                  >
                                    {dataItem?.name}
                                  </MenuItem>
                                ))}
                              </GKSelect>
                            )}
                          />
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                          <Controller
                            name={`bankAccounts.${index}.accountNum`}
                            control={form.control}
                            rules={{
                              required: {
                                value: true,
                                message: "Please enter Account number",
                              },
                            }}
                            render={({ field, fieldState: { error } }) => (
                              <GKTextField
                                {...field}
                                loading={isFetching}
                                requiredField
                                inputLabel="Bank Account Number"
                                error={!!error}
                                helperText={error?.message}
                              />
                            )}
                          />
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                          <Controller
                            name={`bankAccounts.${index}.confirmAccountNum`}
                            control={form.control}
                            rules={{
                              required: {
                                value: true,
                                message: "Please confirm Account number",
                              },
                            }}
                            render={({ field, fieldState: { error } }) => (
                              <GKTextField
                                {...field}
                                loading={isFetching}
                                requiredField
                                inputLabel="Bank Account Number"
                                error={!!error}
                                helperText={error?.message}
                              />
                            )}
                          />
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                          <Controller
                            name={`bankAccounts.${index}.ifsc`}
                            control={form.control}
                            rules={{
                              required: {
                                value: true,
                                message: "Please enter Bank IFSC",
                              },
                            }}
                            render={({ field, fieldState: { error } }) => (
                              <GKTextField
                                {...field}
                                loading={isFetching}
                                requiredField
                                inputLabel="IFSC Code"
                                error={!!error}
                                helperText={error?.message}
                              />
                            )}
                          />
                        </Grid2>
                      </Grid2>
                    );
                  })}
                </Grid2>
                <Grid2 size={12} display={"flex"} alignItems={"center"} gap={1}>
                  <Typography
                    color="var(--neutral-500)"
                    fontSize={12}
                    whiteSpace={"nowrap"}
                    fontWeight={600}
                  >
                    NOMINEE DETAILS
                  </Typography>
                  <Divider sx={{ flex: 1 }} />
                </Grid2>
                <Grid2 size={12}>
                  {nomineeFields.map((item: any, index: number) => (
                    <Grid2 container size={12} spacing={1} my={1} key={item.id}>
                      <Grid2 size={{ xs: 12, md: 4 }}>
                        <Controller
                          name={`nominees.${index}.name`}
                          control={form.control}
                          render={({ field }) => (
                            <GKTextField {...field} inputLabel="Nominee Name" />
                          )}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, md: 4 }}>
                        <Controller
                          name={`nominees.${index}.percentage`}
                          control={form.control}
                          render={({ field }) => (
                            <GKTextField
                              {...field}
                              inputLabel="Nominee Percentage"
                            />
                          )}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, md: 3 }}>
                        <Controller
                          name={`nominees.${index}.relationship`}
                          control={form.control}
                          render={({ field }) => (
                            <GKTextField {...field} inputLabel="Relationship" />
                          )}
                        />
                      </Grid2>
                      <Grid2
                        size={{ xs: 12, md: 1 }}
                        alignSelf={"center"}
                        mt={2}
                      >
                        <IconButton
                          color="error"
                          disabled={nomineeFields.length === 1}
                          onClick={() => removeNominee(index)}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Grid2>
                    </Grid2>
                  ))}
                  <Button
                    variant="outlined"
                    onClick={() =>
                      appendNominee({
                        name: "",
                        percentage: "",
                        relationship: "",
                      })
                    }
                  >
                    Add Nominee
                  </Button>
                </Grid2>
                <Grid2 size={12} display={"flex"} alignItems={"center"} gap={1}>
                  <Typography
                    color="var(--neutral-500)"
                    fontSize={12}
                    whiteSpace={"nowrap"}
                    fontWeight={600}
                  >
                    PERSONAL INFORMATION
                  </Typography>
                  <Divider sx={{ flex: 1 }} />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="personalDetails.gender"
                    control={form.control}
                    rules={{
                      required: {
                        value: true,
                        message: "Please select gender",
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <GKSearchSelect
                        {...field}
                        loading={isFetching}
                        disableClearable={false}
                        inputLabel="Gender"
                        options={PERSONAL_GENDER || []}
                        onChange={(event, val) => {
                          field.onChange(val?.value);
                        }}
                        getOptionLabel={(option) => option.name || option}
                        requiredField
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="personalDetails.maritalStatus"
                    control={form.control}
                    rules={{
                      required: {
                        value: true,
                        message: "Please select marital status",
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <GKSelect
                        {...field}
                        requiredField
                        error={!!error}
                        helperText={error?.message}
                        inputLabel="Marital Status"
                      >
                        <MenuItem style={{ display: "none" }} value="">
                          Select Marital Status
                        </MenuItem>
                        {MARITAL_STATUS?.map((data) => (
                          <MenuItem value={data.value} key={data.value}>
                            {data.name}
                          </MenuItem>
                        ))}
                      </GKSelect>
                    )}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="personalDetails.fatherName"
                    control={form.control}
                    rules={{
                      required: {
                        value: true,
                        message: "Please enter father's name",
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <GKTextField
                        {...field}
                        loading={isFetching}
                        requiredField
                        error={!!error}
                        helperText={error?.message}
                        inputLabel="Father's Name"
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="personalDetails.motherName"
                    control={form.control}
                    rules={{
                      required: {
                        value: true,
                        message: "Please enter mother's name",
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <GKTextField
                        {...field}
                        loading={isFetching}
                        requiredField
                        error={!!error}
                        helperText={error?.message}
                        inputLabel="Mother's Name"
                      />
                    )}
                  />
                </Grid2>
                <Grid2
                  size={{ xs: 12, md: 6 }}
                  display={"flex"}
                  flexDirection={"column"}
                  alignItems={"flex-end"}
                  gap={1}
                >
                  <Controller
                    name="personalDetails.dateOfBirth"
                    control={form.control}
                    render={({ field }) => (
                      <GKDatePicker
                        {...field}
                        disableFuture
                        inputLabel="Date Of Birth"
                        value={dayjs(field.value)}
                        onChange={(value) => {
                          field.onChange(dayjs(value).format("YYYY-MM-DD"));
                        }}
                        minDate={dayjs("01-01-1901")}
                        slotProps={{
                          textField: {
                            error: false,
                            helperText: false,
                          },
                        }}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="personalDetails.countryOfBirth"
                    control={form.control}
                    rules={{
                      required: {
                        value: true,
                        message: "Please select country of birth",
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <GKSearchSelect
                        {...field}
                        loading={isFetching}
                        inputLabel="Country of Birth"
                        requiredField
                        error={!!error}
                        helperText={error?.message}
                        options={countriesToShow}
                        getOptionLabel={(e) => e.name || e}
                        onChange={(e, val) => {
                          field.onChange(val);
                        }}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="personalDetails.placeOfBirth"
                    control={form.control}
                    rules={{
                      required: {
                        value: true,
                        message: "Please enter place of birth",
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <GKTextField
                        {...field}
                        loading={isFetching}
                        requiredField
                        error={!!error}
                        helperText={error?.message}
                        inputLabel="Place Of Birth"
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={12} display={"flex"} alignItems={"center"} gap={1}>
                  <Typography
                    color="var(--neutral-500)"
                    fontSize={12}
                    whiteSpace={"nowrap"}
                    fontWeight={600}
                  >
                    PROFESSIONAL & FINANCIAL
                  </Typography>
                  <Divider sx={{ flex: 1 }} />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="personalDetails.occupationType"
                    control={form.control}
                    rules={{
                      required: {
                        value: true,
                        message: "Please select occupation type",
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <GKSearchSelect
                        {...field}
                        loading={isFetching}
                        inputLabel="Occupation Type"
                        requiredField
                        error={!!error}
                        helperText={error?.message}
                        options={occupationOptions || []}
                        getOptionLabel={(e) => e.name || e}
                        onChange={(e, val) => {
                          field.onChange(val.value);
                        }}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="personalDetails.incomeSlab"
                    control={form.control}
                    rules={{
                      required: {
                        value: true,
                        message: "Please select income slab",
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <GKSearchSelect
                        {...field}
                        loading={isFetching}
                        inputLabel="Income Slab"
                        requiredField
                        error={!!error}
                        helperText={error?.message}
                        options={incomeSlabOptions || []}
                        getOptionLabel={(e) => e.name || e}
                        onChange={(e, val) => {
                          field.onChange(val.value);
                        }}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="personalDetails.pepDetails"
                    control={form.control}
                    rules={{
                      required: {
                        value: true,
                        message: "Please select PEP details",
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <GKSearchSelect
                        {...field}
                        loading={isFetching}
                        inputLabel="PEP Details"
                        requiredField
                        error={!!error}
                        helperText={error?.message}
                        options={pepDetailsOptions || []}
                        getOptionLabel={(e) => e.name || e}
                        onChange={(e, val) => {
                          field.onChange(val.value);
                        }}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={12} display={"flex"} alignItems={"center"} gap={1}>
                  <Typography
                    color="var(--neutral-500)"
                    fontSize={12}
                    whiteSpace={"nowrap"}
                    fontWeight={600}
                  >
                    RESIDENT & TAX
                  </Typography>
                  <Divider sx={{ flex: 1 }} />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="personalDetails.residentialStatus"
                    control={form.control}
                    rules={{
                      required: {
                        value: true,
                        message: "Please select resident status",
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <GKSearchSelect
                        {...field}
                        loading={isFetching}
                        inputLabel="Resident status"
                        requiredField
                        error={!!error}
                        helperText={error?.message}
                        options={[
                          {
                            name: "Resident Individual",
                            value: "resident_individual",
                          },
                        ]}
                        getOptionLabel={(e) => e.name || e}
                        onChange={(e, val) => {
                          field.onChange(val.value);
                        }}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="personalDetails.nationalityCountry"
                    control={form.control}
                    rules={{
                      required: {
                        value: true,
                        message: "Please select nationality",
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <GKSearchSelect
                        {...field}
                        loading={isFetching}
                        inputLabel="Nationality"
                        requiredField
                        error={!!error}
                        helperText={error?.message}
                        options={countriesToShow}
                        getOptionLabel={(e) => e.name || e}
                        onChange={(e, val) => {
                          field.onChange(val);
                        }}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="personalDetails.citizenshipCountries"
                    control={form.control}
                    rules={{
                      required: {
                        value: true,
                        message: "Please select citizenship countries",
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <GKSearchSelect
                        {...field}
                        loading={isFetching}
                        inputLabel="Citizenship Countries"
                        requiredField
                        error={!!error}
                        helperText={error?.message}
                        options={countriesToShow}
                        getOptionLabel={(e) => e.name || e}
                        onChange={(e, val) => {
                          field.onChange(val);
                        }}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="personalDetails.taxResidencyOtherThanIndia"
                    control={form.control}
                    render={({ field, fieldState: { error } }) => (
                      <GKSearchSelect
                        {...field}
                        loading={isFetching}
                        inputLabel="Tax Residency Other Than India?"
                        error={!!error}
                        helperText={error?.message}
                        value={
                          field.value === true
                            ? { value: true, name: "Yes" }
                            : field.value === false
                              ? { value: false, name: "No" }
                              : null // ✅ explicitly null when no value
                        }
                        options={[
                          { value: true, name: "Yes" },
                          { value: false, name: "No" },
                        ]}
                        getOptionLabel={(e) => e?.name ?? e}
                        isOptionEqualToValue={(option, value) =>
                          option?.value === value?.value
                        } // ✅ add this
                        onChange={(_, val) => {
                          field.onChange(val?.value ?? null);
                        }}
                      />
                    )}
                  />
                </Grid2>
                <Show.When
                  isTrue={form.watch(
                    "personalDetails.taxResidencyOtherThanIndia",
                  )}
                >
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="personalDetails.nonIndianTaxResidencyCountries"
                      control={form.control}
                      rules={{
                        required: {
                          value: true,
                          message: "Please select foreign tax country",
                        },
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <GKSearchSelect
                          {...field}
                          loading={isFetching}
                          inputLabel="Foreign Tax Country"
                          requiredField
                          error={!!error}
                          helperText={error?.message}
                          options={countriesToShow}
                          getOptionLabel={(e) => e.name || e}
                          onChange={(e, val) => {
                            field.onChange(val);
                          }}
                        />
                      )}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="personalDetails.nonIndianTaxResidencyTaxIdNumber"
                      control={form.control}
                      rules={{
                        required: {
                          value: true,
                          message: "Please enter foreign tax id",
                        },
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <GKTextField
                          {...field}
                          loading={isFetching}
                          inputLabel="Foreign Tax Id"
                          requiredField
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid2>
                </Show.When>
              </Grid2>
            </form>
          </Grid2>
          <Grid2 size={12} p={2} textAlign={"right"}>
            <Button
              variant="contained"
              endIcon={<RightArrowIcon />}
              type="submit"
            >
              Next
            </Button>
          </Grid2>
        </Show.When>
        <Show.When isTrue={form.getValues("currentStep") === 1}>
          <Grid2 size={12} px={1} py={2} container>
            {stepperData.map((dataItem, index) => (
              <Accordion
                key={index}
                variant="outlined"
                sx={{ width: "100%", border: "none", py: 2 }}
                expanded={expanded === index}
                onChange={
                  !dataItem.isCompleted ? handleChange(index) : () => {}
                }
              >
                <AccordionSummary
                  expandIcon={!dataItem.isCompleted && <Down1Icon />}
                  aria-controls={`panel-${index}-content`}
                  id={`panel-${index}-header`}
                >
                  <Box display={"flex"} alignItems={"center"} gap={2}>
                    {dataItem.isCompleted ? (
                      <SuccessFilledIcon
                        width={20}
                        height={20}
                        color="var(--success-700)"
                      />
                    ) : (
                      <dataItem.icon width={20} height={20} />
                    )}
                    <Box>
                      <Typography fontWeight={600}>{dataItem.title}</Typography>
                      <Typography fontSize={12} color="var(--neutral-500)">
                        {dataItem.subTitle}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails>{dataItem.card}</AccordionDetails>
              </Accordion>
            ))}
          </Grid2>
        </Show.When>
      </Grid2>
    </form>
  );
};

export default AdvisorIncompleteMFKYC;
